// F4.1 — Generadores de mensaje WhatsApp para autorizaciones/confirmaciones de retiro.
// Solo construye texto y abre wa.me (sin API, sin envío automático, sin números hardcodeados).
const money = (n) => (n == null ? "" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const maskImei = (imei) => { if (!imei) return null; const s = String(imei); return "•".repeat(Math.max(0, s.length - 4)) + s.slice(-4); };
const WT_LABEL = {
  VENTA_LUIS_PAGADA: "VENTA GESTIONADA POR EL CONSIGNADOR — YA PAGADA",
  VENTA_LUIS_COBRAR: "VENTA GESTIONADA POR EL CONSIGNADOR — COBRAR EN PUNTOCELL",
  ENTREGA_SIN_COBRO: "ENTREGA AUTORIZADA SIN COBRO",
  OTRO: "OTRO",
};
const equipoLine = (w) => (w.item_type === "accessory"
  ? `${w.item_label || ""}${w.qty ? ` ×${w.qty}` : ""}`.trim()
  : [w.item_label, w.item_detail].filter(Boolean).join(" · "));

export function buildAuthMessage(w) {
  const L = ["📦 *AUTORIZACIÓN DE RETIRO — PUNTOCELL*", ""];
  if (w.owner_name) L.push(`👤 *Autorizado por:* ${w.owner_name}${w.owner_code ? ` (${w.owner_code})` : ""}`);
  const eq = equipoLine(w); if (eq) L.push(`📱 *Equipo:* ${eq}`);
  if (w.imei) L.push(`🔢 *IMEI:* ${maskImei(w.imei)}`);
  if (w.pickup_name) L.push(`🙋 *Persona autorizada:* ${w.pickup_name}`);
  if (w.pickup_phone) L.push(`📞 *Teléfono:* ${w.pickup_phone}`);
  if (w.pickup_document) L.push(`🪪 *Documento:* ${w.pickup_document}`);
  L.push(`📋 *Modalidad:* ${WT_LABEL[w.withdrawal_type] || w.withdrawal_type}`);
  if (w.notes) L.push(`📝 *Instrucciones:* ${w.notes}`);
  if (w.id) L.push(`🆔 *Autorización:* ${w.id}`);
  L.push("⏳ *Estado:* PENDIENTE DE ENTREGA", "");
  if (w.withdrawal_type === "VENTA_LUIS_PAGADA") L.push("✅ *NO COBRAR — YA PAGADO*");
  else if (w.withdrawal_type === "VENTA_LUIS_COBRAR") L.push(`💵 *COBRAR ${money(w.amount_to_collect)} ANTES DE ENTREGAR*`);
  else if (w.withdrawal_type === "ENTREGA_SIN_COBRO") L.push("✅ *ENTREGAR SIN COBRO*");
  L.push("⚠️ La entrega física debe ser confirmada por PuntoCell.");
  return L.join("\n");
}

export function buildConfirmMessage(w) {
  const L = ["✅ *RETIRO COMPLETADO — PUNTOCELL*", ""];
  if (w.owner_name) L.push(`👤 *Consignador:* ${w.owner_name}${w.owner_code ? ` (${w.owner_code})` : ""}`);
  const eq = equipoLine(w); if (eq) L.push(`📱 *Equipo:* ${eq}`);
  if (w.imei) L.push(`🔢 *IMEI:* ${maskImei(w.imei)}`);
  if (w.pickup_name) L.push(`🙋 *Retirado por:* ${w.pickup_name}`);
  if (w.id) L.push(`🆔 *Autorización:* ${w.id}`);
  if (w.confirmed_at) L.push(`🕐 *Entregado:* ${fmtDate(w.confirmed_at)}`);
  if (w.confirmed_by) L.push(`👨‍💼 *Entregado por:* ${w.confirmed_by}`);
  if (w.withdrawal_type === "VENTA_LUIS_COBRAR" && w.amount_to_collect != null) L.push(`💵 *Cobro indicado:* ${money(w.amount_to_collect)} (informativo)`);
  else if (w.withdrawal_type === "VENTA_LUIS_PAGADA") L.push("✅ *Estaba pagado — no se cobró*");
  L.push("", "✅ Equipo entregado correctamente.");
  return L.join("\n");
}

export const openWhatsApp = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");

// F6 — Solicitud/confirmación de PAGO o RETIRO monetario del consignador.
const PAY_METHOD = { EFECTIVO: "Efectivo", ZELLE: "Zelle", WIRE: "Wire / Transferencia bancaria", USDT: "USDT", OTRO: "Otro" };

export function buildPaymentRequestMessage(p) {
  const d = p.details || {};
  const L = ["💰 *SOLICITUD DE PAGO — PUNTOCELL*", ""];
  if (p.owner_name) L.push(`👤 *Consignador:* ${p.owner_name}${p.owner_code ? ` (${p.owner_code})` : ""}`);
  L.push(`💵 *Monto:* ${money(p.amount)}`);
  L.push(`🏷️ *Método:* ${PAY_METHOD[p.method] || p.method}`);
  if (p.method === "EFECTIVO" && d.pickup_name) L.push(`🙋 *Persona autorizada:* ${d.pickup_name}${d.pickup_phone ? ` · ${d.pickup_phone}` : ""}${d.pickup_document ? ` · ${d.pickup_document}` : ""}`);
  if (p.method === "ZELLE" && (d.recipient || d.zelle_contact)) L.push(`📧 *Zelle:* ${[d.recipient, d.zelle_contact].filter(Boolean).join(" · ")}`);
  if (p.method === "WIRE" && (d.beneficiary || d.bank || d.reference)) L.push(`🏦 *Banco:* ${[d.beneficiary, d.bank, d.reference].filter(Boolean).join(" · ")}`);
  if (p.method === "USDT" && (d.network || d.wallet)) L.push(`🪙 *USDT:* ${[d.network, d.wallet].filter(Boolean).join(" · ")}`);
  if (p.method === "OTRO" && (d.method_desc || d.instructions)) L.push(`📝 *Detalle:* ${[d.method_desc, d.instructions].filter(Boolean).join(" · ")}`);
  if (p.note) L.push(`📝 *Nota:* ${p.note}`);
  if (p.id) L.push(`🆔 *Referencia:* ${p.id}`);
  L.push("⏳ *Estado:* PENDIENTE");
  return L.join("\n");
}

export function buildPaymentConfirmMessage(p) {
  const c = p.confirmation || {};
  const L = ["✅ *PAGO CONFIRMADO — PUNTOCELL*", ""];
  if (p.owner_name) L.push(`👤 *Consignador:* ${p.owner_name}${p.owner_code ? ` (${p.owner_code})` : ""}`);
  L.push(`💵 *Monto:* ${money(p.amount)}`);
  L.push(`🏷️ *Método:* ${PAY_METHOD[p.method] || p.method}`);
  if (c.reference) L.push(`🔖 *Referencia:* ${c.reference}`);
  if (c.txid) L.push(`🔗 *TxID:* ${c.txid}`);
  if (p.id) L.push(`🆔 *ID:* ${p.id}`);
  L.push("👨‍💼 *Confirmado por PuntoCell*");
  if (c.at || p.confirmed_at) L.push(`🕐 *Fecha/hora:* ${fmtDate(c.at || p.confirmed_at)}`);
  return L.join("\n");
}
